import {
  AdminNotAllowedError,
  completeSignIn,
  hasSession,
  reloadOnAccountChange,
  signIn,
  signOut,
  wasSignedOutElsewhere,
} from "./auth";

type StartupError = {
  action: { label: string; run: () => void };
  heading?: string;
  message: string;
};

/**
 * Shown when the app cannot start: React is not loaded yet, so this is plain
 * DOM, styled by `stylesheets/startup-error.css`. Every failure offers a way out, so an API outage or a failed sign-in
 * never leaves a blank page. The button starts one attempt only; an attempt
 * that fails renders a fresh screen.
 */
function renderStartupError({
  error: { action, heading: headingText, message },
  root,
}: {
  error: StartupError;
  root: Element;
}): void {
  const container = document.createElement("main");
  container.className = "startup-error";
  container.setAttribute("role", "alert");

  const card = document.createElement("div");
  card.className = "startup-error__card";

  const eyebrow = document.createElement("p");
  eyebrow.className = "startup-error__eyebrow";
  eyebrow.textContent = "Zymego Admin Portal";

  const heading = document.createElement("h1");
  heading.className = "startup-error__heading";
  heading.textContent = headingText ?? "The Admin Portal could not start";

  const paragraph = document.createElement("p");
  paragraph.className = "startup-error__message";
  paragraph.textContent = message;

  const button = document.createElement("button");
  button.className = "startup-error__button";
  button.type = "button";
  button.textContent = action.label;
  button.addEventListener(
    "click",
    () => {
      button.disabled = true;
      action.run();
    },
    { once: true },
  );

  card.append(eyebrow, heading, paragraph, button);
  container.append(card);
  root.replaceChildren(container);
}

/**
 * Starts the admin app: finishes a sign-in or sign-out in progress, or checks
 * the session and either renders the app or sends the admin to sign in.
 */
async function bootstrap({
  renderApp,
  root,
}: {
  renderApp: () => void;
  root: Element;
}): Promise<void> {
  const { pathname } = globalThis.location;
  const retry = {
    label: "Try again",
    run: () => void bootstrap({ renderApp, root }),
  };

  const startApp = () => {
    // Clears a startup error left by an earlier attempt.
    root.replaceChildren();
    reloadOnAccountChange();
    renderApp();
  };

  const startSignIn = async ({
    message,
    selectAccount,
  }: {
    message: string;
    selectAccount?: boolean;
  }) => {
    try {
      await signIn({ selectAccount });
    } catch (error) {
      console.error("Admin sign-in could not start:", error);
      renderStartupError({
        error: {
          action: {
            label: "Sign in",
            run: () => void startSignIn({ message, selectAccount }),
          },
          message,
        },
        root,
      });
    }
  };

  if (pathname === "/logout") {
    try {
      await signOut();
    } catch (error) {
      console.error("Admin sign-out failed:", error);
      renderStartupError({
        error: {
          action: retry,
          message:
            "Signing out did not go through, so you are still signed in. Try again.",
        },
        root,
      });
      return;
    }

    await startSignIn({
      message: "You are signed out, but signing in again could not start.",
      selectAccount: true,
    });
    return;
  }

  if (pathname === "/auth/callback") {
    let returnTo: string;

    try {
      returnTo = await completeSignIn();
    } catch (error) {
      console.error("Admin sign-in failed:", error);
      renderStartupError({
        error:
          error instanceof AdminNotAllowedError
            ? {
                action: {
                  label: "Use another account",
                  run: () =>
                    void startSignIn({
                      message: "Signing in could not start.",
                      selectAccount: true,
                    }),
                },
                // Says nothing about the allow list or the account.
                heading: "Couldn't sign you in",
                message: "Contact your administrator or use another account.",
              }
            : {
                action: {
                  label: "Sign in again",
                  run: () =>
                    void startSignIn({
                      message: "Signing in could not start.",
                    }),
                },
                message: "Signing in did not go through.",
              },
        root,
      });
      return;
    }

    // Replace the callback URL, which holds the spent code.
    globalThis.history.replaceState(undefined, "", returnTo);
    startApp();
    return;
  }

  if (wasSignedOutElsewhere()) {
    // Waits for the admin; a sign-in in another tab reloads this one.
    reloadOnAccountChange();
    renderStartupError({
      error: {
        action: {
          label: "Sign in",
          run: () =>
            void startSignIn({
              message: "Signing in could not start.",
              selectAccount: true,
            }),
        },
        heading: "You are signed out",
        message: "You signed out of the Admin Portal in another tab.",
      },
      root,
    });
    return;
  }

  try {
    if (await hasSession()) {
      startApp();
    } else {
      await signIn();
    }
  } catch (error) {
    console.error("Admin sign-in check failed:", error);
    renderStartupError({
      error: {
        action: retry,
        message: "The Admin Portal could not reach the server.",
      },
      root,
    });
  }
}

export { bootstrap };
